/*
 * The durable transcript — worker-side, because it is network IO.
 *
 * A conversation's messages are not the browser's to keep. They live in the per-user conversation
 * Durable Object, are archived to D1, and are read back by the AI's own memory consolidation and by
 * the console's transcript browser. A second store in a tab would be a second source of truth for
 * what was said, which is the defect class this codebase keeps removing.
 *
 * So this is a thin client over two existing surfaces rather than a store:
 *   • WRITE — `POST /conv/append`, protobuf, one message at a time. The route validates the parts
 *     vocabulary and budgets before the DO sees it, so a malformed append is refused here rather
 *     than discovered at render.
 *   • READ — `AiAdmin.GetTranscript`, which reads the D1 archive seq-ascending.
 *
 * WHY WRITE AND READ ARE DIFFERENT DOORS, since that looks asymmetric: the DO is the authority for
 * ordering (it assigns `seq`) but keeps no queryable history; D1 is the archive that can be paged.
 * Appending through the archive would lose the ordering guarantee, and reading through the DO would
 * mean giving it a query surface it deliberately does not have.
 *
 * DOM-FREE by construction — it is reached only through the worker entry.
 */
import { create, toBinary, fromBinary } from '@bufbuild/protobuf';
import { ConvMessageSchema, ConvRole, AppendMessageRequestSchema, AppendMessageReplySchema, } from '@vitesse/proto/vitesse/ai/v1/conversation_pb.js';
import { referencePart } from './references';
/** `content_json` is the AI-SDK part model — an ARRAY of parts, opaque to the backend. A bare string
 *  would be accepted by nothing downstream, so plain text is still a one-element text part. */
/**
 * Persisted thinking is CAPPED, and the cap protects the answer rather than the disk.
 *
 * A part over the door's 32 KB limit is refused, and a refusal fails the whole message — so an
 * unbounded thought would take the model's ANSWER down with it. A measured DeepSeek turn thought
 * 5,425 characters on a one-line question, so this ceiling is reachable in ordinary use.
 *
 * Head+tail rather than a plain cut: the opening frames the problem and the closing reaches the
 * conclusion, and the middle of a chain of thought is the part nobody reads. Mirrors the budget
 * this repo already applies to sandbox stdio (`STDIO_MAX_CHARS`), and must stay at or under the
 * worker's `REASONING_MAX_CHARS` — the validator is the backstop, this is the producer bound it
 * expects.
 */
const REASONING_MAX = 24 * 1024;
const ELIDED = '\n\n… thinking truncated …\n\n';
function boundReasoning(text) {
    if (text.length <= REASONING_MAX)
        return { text, truncated: false };
    const keep = Math.floor((REASONING_MAX - ELIDED.length) / 2);
    return { text: `${text.slice(0, keep)}${ELIDED}${text.slice(-keep)}`, truncated: true };
}
function asParts(text, reasoning, reasoningMs, telemetry, files, references, settings) {
    const parts = [];
    if (reasoning) {
        const bounded = boundReasoning(reasoning);
        parts.push({
            type: 'reasoning',
            text: bounded.text,
            ...(bounded.truncated ? { truncated: true } : {}),
            // Absent stays absent — a resumed turn with no clock says "Thought", never "Thought for 0s".
            ...(reasoningMs !== undefined ? { ms: reasoningMs } : {}),
        });
    }
    parts.push({ type: 'text', text });
    // What the person attached, as references — the bytes are already in the blob store (the append
    // door refuses a ref it has not seen uploaded), and the fold reads these back onto the turn.
    for (const f of files ?? [])
        parts.push({ type: 'file', sha256: f.sha256, mediaType: f.mediaType, size: f.size, filename: f.filename });
    // What the person pointed at with @, with what the model was told about each — so a reload, a
    // fork or a branch replays the same facts. The link in the text is where it was typed.
    for (const r of references ?? [])
        parts.push(referencePart(r));
    // Who it was asked of or answered by — before the measurements, which stay last.
    const chosen = settingsData(settings);
    if (chosen)
        parts.push({ type: 'data-turn-settings', data: chosen });
    /*
     * The turn's own measurements, LAST — they annotate what came before rather than being read.
     *
     * Persisted because a reopened conversation has to be the same conversation. These lived only in
     * the tab that produced them, so leaving a thread and returning silently dropped its TTFT, average
     * latency and running cost; the header emptied and refilled only if you sent another message.
     * Absent fields stay absent, so a provider that reported no cost still reports none on reload.
     */
    if (telemetry) {
        parts.push({
            type: 'data-turn-telemetry',
            data: {
                totalMs: telemetry.totalMs,
                ...(telemetry.ttftMs !== undefined ? { ttftMs: telemetry.ttftMs } : {}),
                ...(telemetry.costUsd !== undefined ? { costUsd: telemetry.costUsd } : {}),
                ...(telemetry.tokens !== undefined ? { tokens: telemetry.tokens } : {}),
                ...(telemetry.model ? { model: telemetry.model } : {}),
                ...(telemetry.stopped ? { stopped: true } : {}),
            },
        });
    }
    return JSON.stringify(parts);
}
/** The settings a part carries: only the fields that are set, and nothing when none is. */
function settingsData(s) {
    if (!s)
        return undefined;
    const d = {
        ...(s.expertId ? { expertId: s.expertId } : {}),
        ...(s.model ? { model: s.model } : {}),
        ...(s.effort ? { effort: s.effort } : {}),
    };
    return Object.keys(d).length ? d : undefined;
}
/** A person's message as content parts — its text and the files attached to it. The same shape an
 *  ordinary append writes, for the one other writer of a user message: a branch's edited message. */
export function userContent(text, files, references, settings) {
    return asParts(text, undefined, undefined, undefined, files, references, settings);
}
/** The inverse, tolerant of every shape the archive might hold: the parts array, a `{parts}` wrapper,
 *  or (from older rows) a bare string. Best-effort by design — a transcript that fails to render is
 *  worse than one that renders a little loosely. */
export function textOf(contentJson) {
    try {
        const v = JSON.parse(contentJson);
        const parts = Array.isArray(v) ? v : v?.parts;
        if (Array.isArray(parts)) {
            return parts
                .filter((p) => !!p && typeof p === 'object')
                .filter((p) => p.type === 'text')
                .map((p) => p.text ?? '')
                .join('');
        }
        return typeof v === 'string' ? v : '';
    }
    catch {
        return contentJson; // not JSON at all — show it rather than showing nothing
    }
}
/**
 * Append one message. Returns the DO-assigned `seq`.
 *
 * Throws on refusal, and deliberately: an append that silently fails produces a transcript the model
 * is later re-sent WITHOUT the turn that actually happened, which is worse than a visible error.
 */
export async function appendMessage(o, conversationId, role, text, 
/** What the model thought on the way to this answer, and how long it took. Optional because most
 *  models do not think; when present it is what makes the disclosure survive a reload. */
reasoning, reasoningMs, telemetry, 
/** Files the person attached to this message, already uploaded (see `uploadFile`). */
files, 
/** What the person referenced with @ — see `references.ts`. */
references, 
/** Who the turn was asked of (a question) or answered by (an answer) — see `TurnSettings`. */
settings) {
    return appendParts(o, conversationId, role, asParts(text, reasoning, reasoningMs, telemetry, files, references, settings));
}
/**
 * Upload one attachment to the conversation's blob store, BEFORE the message that references it.
 *
 * Same door the tool artifacts use; the sha is computed server-side and is what the message part
 * carries. Null on refusal or network failure — the caller decides whether the message goes
 * without the file, and says so, rather than a reference the door would then reject.
 */
export async function uploadFile(o, conversationId, file) {
    const mediaType = file.type.split(';')[0].trim() || 'application/octet-stream';
    const parked = await parkBlob(o, conversationId, new Uint8Array(await file.arrayBuffer()), file.name, mediaType);
    return parked ? { sha256: parked.sha256, size: parked.size, mediaType, filename: file.name } : null;
}
/** Attachment types whose bytes are text the model can be handed as text. Everything else is
 *  referenced by name only — see `attachmentProse`. */
export function inlineable(mediaType) {
    const m = mediaType.toLowerCase().split(';')[0].trim();
    return m.startsWith('text/') || m === 'application/json';
}
/** How much of an attached text file the prompt carries. Past this it is cut and says so — a
 *  10 MB log pasted whole into every later prompt would spend the turn's budget on the file. */
export const INLINE_MAX_CHARS = 64 * 1024;
/**
 * What the MODEL is told about a message's attachments, appended to the message text in the prompt
 * mirror.
 *
 * The transport carries plain text, so a text file travels as its content, fenced and named, and
 * anything else — a picture, a PDF — travels as a description that says plainly it cannot be read
 * here. The alternative, silence, is what produced a model insisting there was no attachment while
 * the chip sat in the composer.
 *
 * "Cannot be read AS TEXT", and a tool may still load it: this sentence is written once for every
 * app and specialist and cannot know which tools the one answering holds. It used to tell every
 * model to answer from the person's words, and the data scientist, which loads an Excel file whole
 * with sandbox.run, had to be told in its prompt to disregard it.
 */
export function attachmentProse(files, texts) {
    const blocks = [];
    for (const f of files) {
        const label = `${f.filename || f.sha256.slice(0, 12)} (${f.mediaType}, ${f.size} bytes)`;
        const body = texts.get(f.sha256);
        if (body !== undefined) {
            const cut = body.length > INLINE_MAX_CHARS;
            blocks.push(`[Attached file: ${label}]\n${cut ? body.slice(0, INLINE_MAX_CHARS) : body}\n[End of ${f.filename || 'file'}${cut ? ` — cut after ${INLINE_MAX_CHARS} characters of ${body.length}` : ''}]`);
        }
        else if (inlineable(f.mediaType)) {
            blocks.push(`[Attached file: ${label} — stored with the conversation; its contents could not be loaded into this prompt.]`);
        }
        else {
            blocks.push(`[Attached file: ${label} — stored with the conversation; a ${f.mediaType} cannot be read as text by this model. If a tool you hold loads such a file, use it; otherwise answer from what the person says about it.]`);
        }
    }
    return blocks.join('\n\n');
}
/**
 * A tool call, recorded BEFORE it runs.
 *
 * `state: 'input-available'` is the phase the Durable Object requires to exist before it will accept
 * an outcome for the same id — it refuses a result whose dispatch is not in the log. That refusal is
 * the point: on this platform a repeated call can mean writing to plant twice, so a crash between
 * dispatching and recording has to leave "this was attempted, outcome unknown" rather than nothing
 * at all. `tool-<name>` is the AI-SDK part type the projection and the phase check both key on.
 */
/**
 * AN AGENT'S ANSWER, REPORTED BACK INTO THE CHAT THAT ASKED FOR IT.
 *
 * The one message a client writes that it did not author. A child run's return is delivered
 * server-side into its parent's conversation; a ROOT has no parent run — the chat is not a run — so
 * nothing on the server knows where its answer belongs, and this tab, which spawned it, does.
 *
 * TWO THINGS TRAVEL, and each closes a separate defect:
 *
 *   • `fromRunId` — so the record says an agent wrote it. Without it the answer arrives in the user
 *     slot indistinguishable from something the person typed, and every reader after a reload drew
 *     it as their own words. The server does not take this on trust: the Durable Object holding both
 *     the log and this user's runs drops any value that does not name a real one.
 *
 *   • a STABLE id, derived from the run — so reporting the same finished agent twice is one row, not
 *     two. Message ids are unique per user and the store echoes the existing seq on a repeat, which
 *     is exactly what a redelivered return needs. Reopening a thread used to re-report every
 *     finished agent in it, and the model dutifully answered the same return a second time.
 */
export async function appendReturn(o, conversationId, text, fromRunId) {
    return appendParts(o, conversationId, 'user', asParts(text), { id: `ret-${fromRunId}`, fromRunId });
}
export async function appendToolCalls(o, conversationId, calls, reasoning, reasoningMs) {
    /*
     * THE THINKING RIDES THE ROW IT LED TO.
     *
     * A step that calls a tool writes no prose, so its reasoning had no row to live on and existed
     * only while it streamed: the moment the call landed, the thinking vanished, and a thread re-read
     * from the archive showed an agent that never thought. Reported exactly that way — "I saw the
     * thinking for half a second and then it gets flattened and removed."
     *
     * Same part shape as a prose row's reasoning (asParts), so the fold reads both the same way.
     */
    const parts = [];
    if (reasoning) {
        const bounded = boundReasoning(reasoning);
        parts.push({ type: 'reasoning', text: bounded.text, ...(bounded.truncated ? { truncated: true } : {}), ...(reasoningMs !== undefined ? { ms: reasoningMs } : {}) });
    }
    for (const c of calls)
        parts.push({
            type: `tool-${c.name}`,
            toolCallId: c.id,
            state: 'input-available',
            // The arguments AS DISPATCHED, parsed — a reconciler reading a dangling call has to be able to
            // see what it would have done. Kept as raw text when the model produced something unparseable,
            // because "it sent this and it was malformed" is the useful record, not an empty object.
            // Bounded like the result side: generated arguments past the door's inline cap archive as a
            // marked head rather than failing the whole dispatch record.
            input: c.argumentsJson.length > TOOL_RESULT_INLINE_MAX
                ? { truncated: true, fullBytes: c.argumentsJson.length, head: c.argumentsJson.slice(0, TOOL_RESULT_PREVIEW) }
                : parseArgs(c.argumentsJson),
        });
    return appendParts(o, conversationId, 'assistant', JSON.stringify(parts));
}
/**
 * The outcome, resolving the record above.
 *
 * Role `tool`, which the wire has always had (`CONV_ROLE_TOOL`) and the client types simply never
 * used. A failure persists as `output-error` rather than being dropped — the transcript never
 * launders history, and a model that is re-sent its context needs to see that the tool failed, not
 * an absence it will read as "never called".
 */
export async function appendToolResult(o, conversationId, callId, name, outcome) {
    /*
     * ARTIFACTS FIRST, because a `file` part must reference a blob that EXISTS.
     *
     * The append door checks that every sha a message references was uploaded to this conversation
     * and refuses the whole message otherwise — which is the right order of operations and means the
     * upload cannot be deferred or done optimistically.
     *
     * Uploaded one at a time rather than in parallel: this runs on a browser that may also be
     * streaming a turn, and a handful of megabytes racing each other is a worse neighbour than the
     * same bytes in sequence. A failure is skipped, not fatal — a transcript that loses a chart
     * still beats a conversation that stops archiving.
     */
    /*
     * ALREADY-STORED ARTIFACTS COST NOTHING HERE — they are references, not work.
     *
     * A server tool parked its own bytes because it ran where the blob store is. Re-uploading them
     * would pay for the same object twice and, worse, could fail — leaving a tool that succeeded
     * unable to show what it made. They go straight into the list the parts are built from.
     */
    const files = [...(outcome.files ?? [])];
    for (const a of outcome.attachments ?? []) {
        const blob = await parkBlob(o, conversationId, a.bytes, a.filename, a.mediaType);
        if (blob)
            files.push({ sha256: blob.sha256, mediaType: a.mediaType, filename: a.filename, size: blob.size });
    }
    /*
     * RETURNED as well as written, so the LIVE turn can draw the picture too.
     *
     * The sha does not exist until the upload answers, so a caller that wanted to render the
     * artifact immediately had nothing to render — and the alternative, showing it only after a
     * reload, is a conversation that looks different depending on when you look at it.
     */
    const fileParts = files.map((f) => ({ type: 'file', mediaType: f.mediaType, size: f.size, sha256: f.sha256, filename: f.filename }));
    const part = outcome.ok
        ? {
            type: `tool-${name}`,
            toolCallId: callId,
            state: 'output-available',
            output: { message: outcome.message, ...(outcome.data !== undefined ? { data: outcome.data } : {}) },
        }
        : { type: `tool-${name}`, toolCallId: callId, state: 'output-error', errorText: outcome.message };
    /*
     * AN OVERSIZED RESULT IS PARKED, NOT REFUSED.
     *
     * The append door caps a part at 32 KB inline and its refusal says "park it as a blob and
     * reference it" — and nothing did, so the first MCP tool to return a real dataset (Linear's
     * `list_issues`) made every turn that used it fail to archive. The failure arrived as a turn
     * error in the chat, for a tool call that had actually succeeded.
     *
     * So: the FULL payload goes to the conversation's blob store (content-addressed, the same store
     * attachments use, verified by the door), and the inline part keeps a bounded PREVIEW plus the
     * blob reference beside it as a `file` part. The model's CURRENT turn is untouched — it received
     * the full result in memory; what is bounded is the archive, and with it every future turn that
     * replays this conversation, which would otherwise re-send a hundred-kilobyte dump forever.
     *
     * If the blob upload itself fails, the preview alone is appended: a tool result that archives
     * truncated beats a conversation that stops archiving at all.
     */
    if (JSON.stringify(part).length > TOOL_RESULT_INLINE_MAX) {
        if (!outcome.ok) {
            // An error that large is text; bound it and say so in-band.
            const cut = outcome.message.slice(0, TOOL_RESULT_PREVIEW);
            return {
                seq: await appendParts(o, conversationId, 'tool', JSON.stringify([
                    {
                        type: `tool-${name}`,
                        toolCallId: callId,
                        state: 'output-error',
                        errorText: `${cut}\n… (truncated from ${outcome.message.length} chars)`,
                    },
                    ...fileParts,
                ])),
                files,
            };
        }
        const full = JSON.stringify({ message: outcome.message, ...(outcome.data !== undefined ? { data: outcome.data } : {}) });
        const filename = `${name}-result.json`;
        const blob = await parkBlob(o, conversationId, full, filename);
        const slim = {
            type: `tool-${name}`,
            toolCallId: callId,
            state: 'output-available',
            // BOTH fields bounded. An MCP tool's payload usually arrives as `message` — the server hands
            // the tool's text straight through, `data` empty — so a slim part that only previewed `data`
            // while keeping `message` verbatim was still the whole 77 KB. (Exactly how it failed live:
            // the blob uploaded, then the "slim" part was refused at the same door as before.)
            // `preview` is a STRING head of the full JSON — for a reader (or a replayed model) to see the
            // shape of what came back, explicitly not parseable data. `truncated` + `fullBytes` say so.
            output: {
                message: outcome.message.slice(0, 2_000),
                preview: full.slice(0, TOOL_RESULT_PREVIEW),
                truncated: true,
                fullBytes: full.length,
            },
        };
        const parts = [slim];
        if (blob)
            parts.push({ type: 'file', mediaType: 'application/json', size: blob.size, sha256: blob.sha256, filename });
        parts.push(...fileParts);
        return { seq: await appendParts(o, conversationId, 'tool', JSON.stringify(parts)), files };
    }
    return { seq: await appendParts(o, conversationId, 'tool', JSON.stringify([part, ...fileParts])), files };
}
/** Headroom under the door's 32 KB per-part cap: the part's own wrapper counts toward the limit. */
const TOOL_RESULT_INLINE_MAX = 24 * 1024;
/** How much of an oversized payload stays readable inline. */
const TOOL_RESULT_PREVIEW = 8 * 1024;
/** Upload one oversized payload to the conversation's blob store. Null on any failure — the caller
 *  degrades to preview-only rather than letting an R2 hiccup fail the whole archive write. */
async function parkBlob(o, conversationId, body, filename, mediaType = 'application/json') {
    try {
        const post = (headers) => fetch(`${o.aiUrl}/conv/files?conv=${encodeURIComponent(conversationId)}&kind=file`, {
            method: 'POST',
            headers: { ...headers, 'content-type': mediaType, 'x-filename': encodeURIComponent(filename) },
            // A view into a larger buffer would upload the WHOLE buffer: fetch reads the underlying
            // ArrayBuffer, not the view's window. Sliced so the bytes sent are the bytes meant.
            body: typeof body === 'string' ? body : body.slice().buffer,
        });
        let res = await post(o.authHeaders());
        // A JWT that expired a moment ago, recovered as the append recovers it: without this, a message
        // sent as the token turned over lost its attachments ("Could not attach … not sent").
        if (res.status === 401 && o.freshAuthHeaders)
            res = await post(await o.freshAuthHeaders());
        if (!res.ok)
            return null;
        const j = (await res.json());
        return typeof j.sha256 === 'string' && typeof j.size === 'number' ? { sha256: j.sha256, size: j.size } : null;
    }
    catch {
        return null;
    }
}
const parseArgs = (json) => {
    if (!json.trim())
        return {};
    try {
        return JSON.parse(json);
    }
    catch {
        return { raw: json };
    }
};
/** The one write path — every append, whatever it carries, goes through here. */
async function appendParts(o, conversationId, role, contentJson, 
/** For the one message a client writes that another RUN authored: its stable id and its author.
 *  See `appendReturn`. */
authored) {
    const msg = create(ConvMessageSchema, {
        // A stable client id makes the append idempotent — a retried POST after a flaky response cannot
        // duplicate the turn.
        id: authored?.id ?? crypto.randomUUID(),
        ...(authored ? { fromRunId: authored.fromRunId } : {}),
        conversationId,
        role: role === 'user' ? ConvRole.USER : role === 'tool' ? ConvRole.TOOL : ConvRole.ASSISTANT,
        contentJson,
        createdAtNs: BigInt(Date.now()) * 1000000n,
        modality: '', // typed text; voice utterances tag themselves
    });
    const body = toBinary(AppendMessageRequestSchema, create(AppendMessageRequestSchema, { message: msg }));
    const post = (headers) => fetch(`${o.aiUrl}/conv/append`, {
        method: 'POST',
        headers: { 'content-type': 'application/octet-stream', ...headers },
        body: body,
        // SURVIVES THE PAGE GOING AWAY. The last append a turn makes may be the one `pagehide` issues
        // for an answer cut short by a reload — an ordinary fetch is cancelled with the document, and
        // the half-answer the person watched arrive is gone from the record. Rows are bounded well
        // inside the keepalive budget (thinking and tool payloads are capped before they get here).
        keepalive: true,
    });
    let res = await post(o.authHeaders());
    if (res.status === 401 && o.freshAuthHeaders)
        res = await post(await o.freshAuthHeaders());
    if (!res.ok)
        throw new Error(`could not save the message (${res.status})`);
    const reply = fromBinary(AppendMessageReplySchema, new Uint8Array(await res.arrayBuffer()));
    // The universal Result envelope: prose on failure, so the error a user sees explains itself.
    if (reply.result?.error)
        throw new Error(reply.result.error.explanation || reply.result.summary || 'the message was refused');
    return reply.seq;
}
//# sourceMappingURL=transcript.js.map